import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  AUTH_TRANSITION_DURATION_MS,
  AUTH_TRANSITION_NAV_AT_MS,
  AuthTransitionService,
} from '../../services/auth-transition.service';
import { GrainPulseComponent } from '../grain-pulse/grain-pulse.component';

const PEAK_OPACITY = 0.65;
/** CSS class set on <body> during the transition so the auth card
 *  (rendered inside NbAuthComponent) can dissolve via global styles. */
const BODY_CLASS_ACTIVE = 'auth-transition-active';

/**
 * Full-screen overlay that orchestrates the post-login dissolve-and-reveal
 * transition. Hosts a single dedicated grain-pulse instance (independent
 * of the dashboard's instance) and toggles a body class so the auth card
 * can blur + fade out in lock-step with the wave.
 *
 * Choreography (numbers from AUTH_TRANSITION_DURATION_MS = 1500):
 *   t=0          start → body gets `auth-transition-active`, grain wave
 *                begins to rise, auth card starts dissolving
 *   t=750 ms     peak → router navigates to dashboard (masked by wave),
 *                body class removed shortly after so the dashboard
 *                starts fading in *underneath* the receding wave
 *   t=1500 ms    wave fully tailed off; dashboard's own 900ms reveal
 *                still finishing for another ~150ms
 */
/** Delay after the route swap before removing the body class. A short
 *  beat lets Angular mount the dashboard component before the CSS
 *  transition flips it from opacity:0 → 1; without it the very first
 *  frame can flash at full opacity. */
const REVEAL_DELAY_AFTER_NAV_MS = 60;
@Component({
  selector: 'app-auth-transition',
  templateUrl: './auth-transition.component.html',
  styleUrl: './auth-transition.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthTransitionComponent implements AfterViewInit, OnDestroy {
  @ViewChild('pulse') pulseRef?: GrainPulseComponent;

  /** Mirrors body class — drives the overlay's own pointer-events guard
   *  so clicks during the transition don't reach the auth form. */
  active = false;

  private subscription: Subscription | null = null;
  private navTimeoutId: number | null = null;
  private revealTimeoutId: number | null = null;
  private endTimeoutId: number | null = null;

  constructor(
    private readonly authTransition: AuthTransitionService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.subscription = this.authTransition.request$.subscribe(({ redirect }) =>
      this.run(redirect),
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.clearTimers();
    document.body.classList.remove(BODY_CLASS_ACTIVE);
  }

  private run(redirect: string): void {
    this.clearTimers();
    this.active = true;
    document.body.classList.add(BODY_CLASS_ACTIVE);
    this.cdr.markForCheck();

    // Fire the grain wave with the long auth-transition timing. The
    // overlay's GrainPulse instance has listenToService=false so wizard
    // step changes never reach it.
    this.pulseRef?.pulse({
      durationMs: AUTH_TRANSITION_DURATION_MS,
      peakOpacity: PEAK_OPACITY,
    });

    // Navigate at the wave's peak so the swap is hidden under it. Run
    // outside the zone to avoid spurious change-detection cycles during
    // the animation.
    this.zone.runOutsideAngular(() => {
      this.navTimeoutId = window.setTimeout(() => {
        this.navTimeoutId = null;
        this.zone.run(() => this.router.navigateByUrl(redirect));
      }, AUTH_TRANSITION_NAV_AT_MS);

      // Drop the body class shortly after navigation so the dashboard's
      // own CSS fade-in (900ms) starts while the wave is still tailing
      // off — the dashboard emerges from behind the receding wave
      // instead of popping in once the wave is gone.
      this.revealTimeoutId = window.setTimeout(() => {
        this.revealTimeoutId = null;
        this.zone.run(() => {
          document.body.classList.remove(BODY_CLASS_ACTIVE);
          this.cdr.markForCheck();
        });
      }, AUTH_TRANSITION_NAV_AT_MS + REVEAL_DELAY_AFTER_NAV_MS);

      this.endTimeoutId = window.setTimeout(() => {
        this.endTimeoutId = null;
        this.zone.run(() => {
          this.active = false;
          this.cdr.markForCheck();
        });
      }, AUTH_TRANSITION_DURATION_MS);
    });
  }

  private clearTimers(): void {
    if (this.navTimeoutId !== null) {
      window.clearTimeout(this.navTimeoutId);
      this.navTimeoutId = null;
    }
    if (this.revealTimeoutId !== null) {
      window.clearTimeout(this.revealTimeoutId);
      this.revealTimeoutId = null;
    }
    if (this.endTimeoutId !== null) {
      window.clearTimeout(this.endTimeoutId);
      this.endTimeoutId = null;
    }
  }
}
